import React, { type ChangeEventHandler } from 'react';

import { Description, Label, LabelText, StyledRadioButton } from './Styles';

export interface RadioButtonProps {
  disabled?: boolean;
  id?: string;
  checked: boolean;
  name?: string;
  value?: string;
  label?: React.ReactNode;
  onChange: ChangeEventHandler<HTMLInputElement>;
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  suffix?: React.ReactNode;
  description?: string;
  /**
   * Radio buttons belonging to the same "hover group" will get highlighted
   * whenever any one of them is hovered over or focused.
   *
   * Note that as of June 2023 this behavior does not work on Firefox due to how
   * it's implemented — consider it "progressive enhancement".
   */
  hoverGroupName?: string;
}

const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      description,
      disabled = false,
      id,
      checked,
      onChange,
      name,
      value = '',
      label = '',
      alignItems,
      suffix,
      hoverGroupName,
    },
    ref,
  ) => (
    <Label
      $alignItems={alignItems}
      $disabled={disabled}
      $hasDescription={Boolean(description)}
      $hasLabel={Boolean(label)}
      data-cy-radio-button={name ? `${name}-${String(value)}` : ''}
    >
      <StyledRadioButton
        ref={ref}
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        $hoverGroupName={hoverGroupName}
        data-hover-group={hoverGroupName}
      />
      {label && (
        <LabelText $alignItems={alignItems}>
          {label}
          {suffix}
        </LabelText>
      )}
      {description && <Description>{description}</Description>}
    </Label>
  ),
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
